// irr.ts — money-weighted internal rate of return via bisection. Used by
// lib/backtestReturns.ts for the Backtest results' "Anlage-Rendite p.a. (IRR)" headline:
// the effective annualized return of the ACTUAL cashflows (Sparplan contributions paid
// out over time + the final net wealth received at the end) — as opposed to
// fundAnalytics.ts's cagr(), which is time-weighted (what did the FUND'S PRICE do,
// independent of when money went in). Bisection rather than Newton-Raphson: it never
// diverges/overshoots regardless of the starting bracket, and a Sparplan backtest has at
// most a few hundred cashflows — performance is a non-issue either way.
export interface Cashflow {
  date: string; // YYYY-MM-DD
  /** Negative = money leaving the investor's pocket (a contribution); positive = money
   * received (the final net wealth). */
  amountCents: number;
}

const MS_PER_DAY = 86_400_000;
/** Same annualization convention as fundAnalytics.ts's cagr() — average calendar days
 * per year over a 4-year cycle, so the exponent isn't biased by leap days. */
const DAYS_PER_YEAR = 365.25;

function parseIsoUtcMs(dateIso: string): number {
  const [y, m, d] = dateIso.split('-').map(Number);
  return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Net present value of `cashflows` at annual `rate`, discounted from `t0Ms` (the first
 * cashflow's date). */
function npv(cashflows: Cashflow[], rate: number, t0Ms: number): number {
  let sum = 0;
  for (const cf of cashflows) {
    const years = (parseIsoUtcMs(cf.date) - t0Ms) / MS_PER_DAY / DAYS_PER_YEAR;
    sum += cf.amountCents / Math.pow(1 + rate, years);
  }
  return sum;
}

/**
 * Annualized money-weighted return (IRR) of `cashflows`, found by bisecting
 * rate ∈ [-0.999999, 100] (-99.9999 % .. +10 000 % p.a. — wide enough for any realistic
 * Sparplan outcome, including a near-total loss). Returns `null` (never a fabricated
 * number) rather than guessing when:
 *  - there are fewer than 2 cashflows (no rate of return is defined for one data point),
 *  - every cashflow has the same sign (money only ever went one direction — nothing to
 *    annualize a "return" from),
 *  - NPV doesn't change sign across the bracket (bisection has nothing to bracket), or
 *  - the search doesn't converge within `maxIterations`.
 * Dates are annualized as "days since the FIRST cashflow's date, / 365.25" — same
 * dividing convention as fundAnalytics.ts's cagr(), so both p.a. figures shown side by
 * side in the UI use one shared meaning of "per annum".
 */
export function irrBisection(cashflows: Cashflow[], maxIterations = 200, tolerance = 1e-9): number | null {
  if (cashflows.length < 2) return null;
  const hasPositive = cashflows.some((cf) => cf.amountCents > 0);
  const hasNegative = cashflows.some((cf) => cf.amountCents < 0);
  if (!hasPositive || !hasNegative) return null;

  const sorted = [...cashflows].sort((a, b) => a.date.localeCompare(b.date));
  const t0Ms = parseIsoUtcMs(sorted[0]!.date);

  let lo = -0.999999;
  let hi = 100;
  let fLo = npv(sorted, lo, t0Ms);
  let fHi = npv(sorted, hi, t0Ms);
  if (!Number.isFinite(fLo) || !Number.isFinite(fHi)) return null;
  if (fLo === 0) return lo;
  if (fHi === 0) return hi;
  if (fLo > 0 === fHi > 0) return null; // no sign change across the bracket

  let mid = (lo + hi) / 2;
  for (let i = 0; i < maxIterations; i++) {
    mid = (lo + hi) / 2;
    const fMid = npv(sorted, mid, t0Ms);
    if (Math.abs(fMid) < tolerance || hi - lo < tolerance) return mid;
    if (fMid > 0 === fLo > 0) {
      lo = mid;
      fLo = fMid;
    } else {
      hi = mid;
    }
  }
  return mid;
}
